/**
 * Хук построения ECharts-опций для графика прогноза симуляции.
 *
 * Вынесен из PlantingDetailsPage, чтобы изолировать ~400 строк логики
 * формирования серий, осей, tooltip и зон оптимума.
 */
import { useMemo } from 'react';
import type { ForecastMetricKey } from '../constants';
import {
  FORECAST_METRICS,
  HARVEST_DAILY_CHART_SERIES,
  LIFECYCLE_PHASE_LABELS,
  STRESS_FACTOR_LABELS,
  ROTATION_REASON_LABELS,
} from '../constants';
import { normalizeChartNumber, getMetricValue, formatAxisNumber } from '../utils';

const toFiniteChartNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const toChartValue = (value: unknown): number | null => {
  const num = toFiniteChartNumber(value);
  return num == null ? null : normalizeChartNumber(num);
};

interface UseForecastChartParams {
  forecastSeries: any;
  selectedMetric: ForecastMetricKey;
  showOptimalZone: boolean;
  showHarvestDailyBars: boolean;
}

export function useForecastChart({
  forecastSeries,
  selectedMetric,
  showOptimalZone,
  showHarvestDailyBars,
}: UseForecastChartParams): any {
  return useMemo(() => {
    let points = forecastSeries?.timeSeries || [];
    const metric = FORECAST_METRICS.find((m) => m.key === selectedMetric) || FORECAST_METRICS[0];

    // Determine "not planted" ranges
    const notPlantedRanges: Array<{ startDate: string; endDate: string }> = [];
    const firstSenescencePoint = points.find((p: any) => p?.lifecyclePhase === 'senescence') || null;
    let rangeStart: string | null = null;

    points.forEach((point: any, index: number) => {
      const isNotPlanted = point?.vegetationStage === 'not_planted';
      if (isNotPlanted && !rangeStart) rangeStart = point.date;
      const isRangeEnd = rangeStart && (!isNotPlanted || index === points.length - 1);
      if (isRangeEnd) {
        const endPoint = isNotPlanted && index === points.length - 1
          ? point.date
          : points[Math.max(0, index - 1)]?.date;
        if (endPoint) notPlantedRanges.push({ startDate: rangeStart!, endDate: endPoint });
        rangeStart = null;
      }
    });

    // ── Harvest daily bars mode ──
    if (showHarvestDailyBars) {
      return buildHarvestDailyChart(points, notPlantedRanges, firstSenescencePoint);
    }

    if (selectedMetric === 'harvestStandingKg') {
      const curves = [
        ['fruitGreenKg', 'Зелёные', '#389e0d'],
        ['fruitTurningKg', 'Дозревают', '#d48806'],
        ['fruitRipeKg', 'Спелые', '#cf1322'],
        ['fruitOverripeKg', 'Перезревшие', '#722ed1'],
        ['harvestAvailableKg', 'Доступно к сбору', '#08979c'],
        ['harvestCollectedCumulativeKg', 'Собрано всего', '#0958d9'],
        ['harvestLostCumulativeKg', 'Утрачено всего', '#595959'],
      ];
      return {
        tooltip: { trigger: 'axis', valueFormatter: (v: any) => v == null ? '—' : `${Number(v).toFixed(3)} кг/раст.` },
        legend: { type: 'scroll', data: curves.map(c => c[1]) },
        xAxis: { type: 'category', data: points.map((p: any) => p.date) },
        yAxis: { type: 'value', name: 'кг/раст.', min: 0 },
        dataZoom: [{ type: 'inside' }, { type: 'slider', start: 70, end: 100 }],
        series: curves.map(([key, name, color]) => ({ name, type: 'line', showSymbol: false,
          connectNulls: false, itemStyle: { color }, data: points.map((p: any) => toChartValue(p[key])) })),
      };
    }

    // ── Main line chart ──
    return buildMainLineChart(
      points, metric, notPlantedRanges, firstSenescencePoint,
      selectedMetric, showOptimalZone, forecastSeries,
    );
  }, [forecastSeries, selectedMetric, showOptimalZone, showHarvestDailyBars]);
}

// ────────────────────────────────────────
// Harvest daily bar chart builder
// ────────────────────────────────────────
function buildHarvestDailyChart(
  points: any[],
  notPlantedRanges: Array<{ startDate: string; endDate: string }>,
  firstSenescencePoint: any,
) {
  const harvestDailyValuesKg = points.flatMap((point: any) => (
    HARVEST_DAILY_CHART_SERIES
      .map((seriesItem) => Number(point?.[seriesItem.key]))
      .filter(Number.isFinite)
  ));
  const maxDailyValueKg = harvestDailyValuesKg.length > 0
    ? Math.max(...harvestDailyValuesKg.map((value) => Math.abs(value)))
    : 0;
  const useGramScale = maxDailyValueKg > 0 && maxDailyValueKg < 0.05;
  const dailyValueScale = useGramScale ? 1000 : 1;
  const dailyValueUnit = useGramScale ? 'г/раст./день' : 'кг/раст./день';
  const dailyValuePrecision = useGramScale ? 4 : 6;

  return {
    tooltip: {
      trigger: 'axis',
      formatter: (params: any[]) => {
        if (!params?.length) return '';
        const date = params[0]?.axisValue || '';
        let html = `<b>${date}</b>`;
        params.forEach((p) => {
          html += `<br/>${p?.seriesName}: <b>${formatAxisNumber(p?.value, useGramScale ? 3 : 4)}</b> ${dailyValueUnit}`;
        });
        return html;
      },
    },
    legend: { data: HARVEST_DAILY_CHART_SERIES.map((s) => s.label) },
    xAxis: { type: 'category', data: points.map((p: any) => p.date) },
    yAxis: [{
      type: 'value',
      name: dailyValueUnit,
      scale: true,
      axisLabel: { formatter: (value: number) => formatAxisNumber(value, useGramScale ? 2 : 3) },
    }],
    dataZoom: [
      { type: 'inside' },
      { type: 'slider', start: 70, end: 100 },
    ],
    series: HARVEST_DAILY_CHART_SERIES.map((seriesItem) => ({
      name: seriesItem.label,
      type: 'bar',
      barMaxWidth: 14,
      itemStyle: { color: seriesItem.color },
      data: points.map((p: any) => normalizeChartNumber(
        Number(p[seriesItem.key] ?? 0) * dailyValueScale,
        dailyValuePrecision,
      )),
      markArea: seriesItem.key === 'harvestPotentialDailyKg'
        ? {
          silent: true,
          itemStyle: { color: 'rgba(250, 140, 22, 0.12)' },
          label: { show: true, color: '#d46b08', formatter: 'ещё не высажено' },
          data: [
            ...notPlantedRanges.map((range) => ([
              { xAxis: range.startDate },
              { xAxis: range.endDate },
            ])),
            ...(firstSenescencePoint ? [[
              {
                xAxis: firstSenescencePoint.date,
                itemStyle: { color: 'rgba(140, 140, 140, 0.10)' },
                label: { show: true, color: '#595959', formatter: 'увядание / сезон закрыт' },
              },
              { xAxis: points[points.length - 1]?.date },
            ]] : []),
          ],
        }
        : undefined,
      markLine: seriesItem.key === 'harvestPotentialDailyKg' && firstSenescencePoint ? {
        silent: true,
        symbol: 'none',
        lineStyle: { color: '#595959', type: 'dashed', width: 1 },
        label: { formatter: 'старт увядания', color: '#595959' },
        data: [{ xAxis: firstSenescencePoint.date }],
      } : undefined,
    })),
  };
}

// ────────────────────────────────────────
// Main line chart builder
// ────────────────────────────────────────
function buildMainLineChart(
  points: any[],
  metric: typeof FORECAST_METRICS[number],
  notPlantedRanges: Array<{ startDate: string; endDate: string }>,
  firstSenescencePoint: any,
  selectedMetric: ForecastMetricKey,
  showOptimalZone: boolean,
  forecastSeries: any,
) {
  // ── Stress guides ──
  const soilGuide = forecastSeries?.stressGuide?.soil;
  const soilMin = Number(soilGuide?.minSoilMoisturePct);
  const soilOpt = Number(soilGuide?.optSoilMoisturePct);
  const soilMax = Number(soilGuide?.maxSoilMoisturePct);
  const tempGuide = forecastSeries?.stressGuide?.temperature;
  const tempOptMin = Number(tempGuide?.optMinC);
  const tempOptMax = Number(tempGuide?.optMaxC);
  const lightTargetValues = points
    .map((p: any) => Number(p?.dliTargetMol)).filter(Number.isFinite);
  const lightMinValues = points
    .map((p: any) => Number(p?.dliMinMol)).filter(Number.isFinite);
  const lightTargetHoursValues = points
    .map((p: any) => Number(p?.photoperiodTargetHours)).filter((v) => Number.isFinite(v) && v > 0);

  const canShowSoilOptimalZone = showOptimalZone && selectedMetric === 'soilMoisturePct'
    && Number.isFinite(soilMin) && Number.isFinite(soilMax) && soilMax > soilMin;
  const canShowTempOptimalZone = showOptimalZone && selectedMetric === 'tempAvgC'
    && Number.isFinite(tempOptMin) && Number.isFinite(tempOptMax) && tempOptMax > tempOptMin;
  const canShowLightOptimalZone = showOptimalZone && selectedMetric === 'totalDliMol'
    && lightTargetValues.length > 0;
  const canShowPhotoperiodOptimalZone = showOptimalZone && selectedMetric === 'photoperiodHours'
    && lightTargetHoursValues.length > 0;

  // ── Y-axis domain ──
  const metricValues = points
    .map((p: any) => toFiniteChartNumber(getMetricValue(p, selectedMetric)))
    .filter((value): value is number => value != null);
  const dataMin = metricValues.length ? Math.min(...metricValues) : 0;
  const dataMax = metricValues.length ? Math.max(...metricValues) : 1;

  const optimalMin = canShowSoilOptimalZone ? soilMin
    : canShowTempOptimalZone ? tempOptMin
    : canShowLightOptimalZone ? Math.min(...(lightMinValues.length ? lightMinValues : lightTargetValues))
    : canShowPhotoperiodOptimalZone ? Math.min(...lightTargetHoursValues) * 0.7
    : null;
  const optimalMax = canShowSoilOptimalZone ? soilMax
    : canShowTempOptimalZone ? tempOptMax
    : canShowLightOptimalZone ? Math.max(...lightTargetValues)
    : canShowPhotoperiodOptimalZone ? Math.max(...lightTargetHoursValues)
    : null;
  const hasOptimalBounds = Number.isFinite(optimalMin) && Number.isFinite(optimalMax);
  const domainMin = normalizeChartNumber(
    hasOptimalBounds ? Math.min(dataMin, Number(optimalMin)) : dataMin, 6,
  );
  const domainMax = normalizeChartNumber(
    hasOptimalBounds ? Math.max(dataMax, Number(optimalMax)) : dataMax, 6,
  );
  const domainSpan = Math.max(domainMax - domainMin, 1e-6);
  const yAxisPadding = domainSpan * 0.08;

  // ── Tooltip formatter ──
  const tooltipFormatter = buildTooltipFormatter(
    points, selectedMetric, metric, forecastSeries,
    { soilMin, soilOpt, soilMax, tempOptMin, tempOptMax },
  );

  // ── Series ──
    const isIntegerMetric = selectedMetric === 'currentQuantity';
    const isStepMetric = selectedMetric === 'currentQuantity';
    return {
    tooltip: { trigger: 'axis', formatter: tooltipFormatter },
    legend: { data: [metric.label] },
    xAxis: { type: 'category', data: points.map((p: any) => p.date) },
    yAxis: [{
      type: 'value',
      name: metric.unit,
      scale: true,
      min: () => isIntegerMetric ? Math.max(0, Math.floor(domainMin - yAxisPadding)) : normalizeChartNumber(domainMin - yAxisPadding, 6),
      max: () => isIntegerMetric ? Math.ceil(domainMax + yAxisPadding) : normalizeChartNumber(domainMax + yAxisPadding, 6),
      minInterval: isIntegerMetric ? 1 : undefined,
      axisLabel: { formatter: (value: number) => isIntegerMetric ? String(Math.round(value)) : formatAxisNumber(value, 2) },
      axisPointer: { label: { formatter: ({ value }: { value: number }) => isIntegerMetric ? String(Math.round(value)) : formatAxisNumber(value, 2) } },
    }],
    dataZoom: [
      { type: 'inside' },
      { type: 'slider', start: 70, end: 100 },
    ],
    series: [
      // Main data series
      {
        name: metric.label,
        type: 'line',
        smooth: !isStepMetric,
        step: isStepMetric ? 'end' : false,
        showSymbol: false,
        lineStyle: { color: metric.color, width: 2 },
        itemStyle: { color: metric.color },
        data: points.map((p: any) => toChartValue(getMetricValue(p, selectedMetric))),
        markArea: notPlantedRanges.length > 0 ? {
          silent: true,
          itemStyle: { color: 'rgba(250, 140, 22, 0.12)' },
          label: { show: true, color: '#d46b08', formatter: 'ещё не высажено' },
          data: notPlantedRanges.map((range) => ([
            { xAxis: range.startDate }, { xAxis: range.endDate },
          ])),
        } : undefined,
        markLine: canShowSoilOptimalZone && Number.isFinite(soilOpt) ? {
          silent: true, symbol: 'none',
          lineStyle: { color: '#52c41a', type: 'dashed', width: 1 },
          label: { formatter: `оптимум ${soilOpt.toFixed(0)}%`, color: '#389e0d' },
          data: [{ yAxis: soilOpt }],
        } : undefined,
      },
      // Optimal zones
      ...buildOptimalZoneSeries(points, {
        canShowSoilOptimalZone, canShowTempOptimalZone, canShowLightOptimalZone, canShowPhotoperiodOptimalZone,
        soilMin, soilMax, tempOptMin, tempOptMax,
      }),
    ],
  };
}

// ────────────────────────────────────────
// Tooltip formatter
// ────────────────────────────────────────
function buildTooltipFormatter(
  points: any[],
  selectedMetric: ForecastMetricKey,
  metric: typeof FORECAST_METRICS[number],
  forecastSeries: any,
  guides: { soilMin: number; soilOpt: number; soilMax: number; tempOptMin: number; tempOptMax: number },
) {
  const { soilMin, soilMax, tempOptMin, tempOptMax } = guides;

  const isLightMetric = ['totalDliMol', 'photoperiodHours', 'outdoorSunHours', 'astronomicalDaylightHours'].includes(selectedMetric);
  const isEtiolationMetric = selectedMetric === 'etiolation.stress' || selectedMetric === 'etiolation.stemThickness';
  const isSoilMetric = selectedMetric === 'soilMoisturePct';
  const isTempMetric = selectedMetric === 'tempAvgC';
  const isStressMetric = selectedMetric === 'stressMultiplier';
  const isRotationMetric = selectedMetric === 'rotationFactor';
  const isHeightMetric = selectedMetric === 'dimensions.height' || selectedMetric === 'dimensions.trunkDiameter' || selectedMetric === 'dimensions.crownDiameter';
  const isHealthMetric = selectedMetric === 'healthScore';

  const getStressContext = (factorName: string, point: any): string => {
    if (factorName === 'soil') {
      const current = Number(point?.soilMoisturePct);
      if (Number.isFinite(current) && Number.isFinite(soilMin) && Number.isFinite(soilMax)) {
        if (current < soilMin) return ' (засуха/недолив)';
        if (current > soilMax) return ' (переувлажнение/перелив)';
      }
    }
    if (factorName === 'temperature') {
      const current = Number(point?.tempAvgC);
      if (Number.isFinite(current) && Number.isFinite(tempOptMin) && Number.isFinite(tempOptMax)) {
        if (current < tempOptMin) return ' (переохлаждение)';
        if (current > tempOptMax) return ' (перегрев)';
      }
    }
    if (factorName === 'rotation') {
      const reasons = point?.rotationReasons;
      if (Array.isArray(reasons) && reasons.length > 0) {
        if (reasons.includes('ROTATION_FATIGUE_SEVERE')) return ' (сильное утомление)';
        if (reasons.includes('ROTATION_FATIGUE_MODERATE')) return ' (среднее утомление)';
        if (reasons.includes('ALLELOPATHY')) return ' (аллелопатия)';
        if (reasons.includes('PATHOGEN_PRESSURE')) return ' (патогены)';
      }
    }
    return '';
  };

  return (params: any[]) => {
    if (!params?.length) return '';
    const idx = params[0]?.dataIndex ?? 0;
    const point = points[idx] as any;
    const date = params[0]?.axisValue || point?.date || '';
    const value = toFiniteChartNumber(params[0]?.value);
    let html = `<b>${date}</b><br/>${metric.label}: <b>${value != null ? formatAxisNumber(value, 2) : '—'}</b>`;

    // Phase
    if (point?.lifecyclePhase) {
      const phaseLabel = LIFECYCLE_PHASE_LABELS[point.lifecyclePhase] || point.lifecyclePhase;
      html += `<br/><small style="color:#52c41a">🌱 ${phaseLabel}</small>`;
    }

    // Stress breakdown
    if (isStressMetric || isHealthMetric) {
      html += formatStressBreakdown(point, getStressContext);
    }

    // Soil details
    if (isSoilMetric) {
      const soil = Number(point?.soilMoisturePct);
      if (Number.isFinite(soil)) {
        const guide = forecastSeries?.stressGuide?.soil;
        const min = Number(guide?.minSoilMoisturePct);
        const opt = Number(guide?.optSoilMoisturePct);
        const max = Number(guide?.maxSoilMoisturePct);
        if (Number.isFinite(min) && Number.isFinite(opt) && Number.isFinite(max)) {
          html += `<br/><small style="color:#888">Оптимум: ~${opt.toFixed(0)}% (допустимо ${min.toFixed(0)}–${max.toFixed(0)}%)</small>`;
        }
      }
    }

    // Temperature details
    if (isTempMetric) {
      const tMin = Number(point?.tempMinC);
      const tMax = Number(point?.tempMaxC);
      if (Number.isFinite(tMin) && Number.isFinite(tMax)) {
        html += `<br/><small style="color:#888">Мин: ${tMin.toFixed(1)}°C · Макс: ${tMax.toFixed(1)}°C</small>`;
      }
      if (Number.isFinite(tempOptMin) && Number.isFinite(tempOptMax)) {
        html += `<br/><small style="color:#888">Оптимум: ${tempOptMin.toFixed(0)}–${tempOptMax.toFixed(0)}°C</small>`;
      }
    }

    // Light details
    if (isLightMetric) {
      html += formatLightTooltip(point, selectedMetric);
    }

    // Rotation details
    if (isRotationMetric) {
      html += formatRotationTooltip(point);
    }

    // Etiolation details
    if (isEtiolationMetric || isHeightMetric) {
      html += formatEtiolationTooltip(point, isEtiolationMetric, isHeightMetric);
    }

    // Height dimension context
    if (isHeightMetric && selectedMetric === 'dimensions.height') {
      const trunk = Number(point?.dimensions?.trunkDiameter);
      const crown = Number(point?.dimensions?.crownDiameter);
      if (Number.isFinite(trunk) && trunk > 0) html += `<br/><small style="color:#888">Стебель: ⌀${trunk.toFixed(1)} см</small>`;
      if (Number.isFinite(crown) && crown > 0) html += `<br/><small style="color:#888">Крона: ⌀${crown.toFixed(1)} см</small>`;
    }

    // Terminal state
    if (point?.vegetationStage === 'dead') html += `<br/><span style="color:#f5222d">☠ Растение погибло</span>`;
    else if (point?.vegetationStage === 'not_planted') html += `<br/><span style="color:#d46b08">⏳ Ещё не высажено</span>`;

    return html;
  };
}

// ── Tooltip sub-formatters ──

function formatStressBreakdown(point: any, getCtx: (name: string, p: any) => string): string {
  let html = '';
  const sf = point?.stressFactors;
  const sm = Number(point?.stressMultiplier ?? 1);
  if (sf && sm < 0.98) {
    html += `<br/><span style="color:#fa8c16">⚠ Общее угнетение: ${Math.round((1 - sm) * 100)}%</span>`;
    const entries = [
      ['temperature', sf.temperature], ['climateWater', sf.climateWater],
      ['soil', sf.soil], ['light', sf.light],
      ['etiolation', sf.etiolation], ['soilQuality', sf.soilQuality],
      ['soilInteraction', sf.soilInteraction], ['rotation', sf.rotation],
    ].filter(([, v]) => v != null) as [string, number][];
    const stressed = entries.filter(([, v]) => v < 0.97);
    if (stressed.length > 0) {
      html += '<br/><small style="color:#888">Факторы угнетения:</small>';
      for (const [name, val] of stressed) {
        const pct = Math.round((1 - val) * 100);
        const isDom = sf.dominant === name;
        const ctx = getCtx(name, point);
        html += `<br/><small>${isDom ? '▶ ' : '&nbsp;&nbsp;'}${STRESS_FACTOR_LABELS[name] ?? name}${ctx}: ${pct}%${isDom ? ' ◀' : ''}</small>`;
      }
    }
  }
  return html;
}

function formatLightTooltip(point: any, selectedMetric: ForecastMetricKey): string {
  let html = '';
  const naturalDli = toFiniteChartNumber(point?.naturalDliMol);
  const supplementalDli = toFiniteChartNumber(point?.supplementalDliMol);
  const outdoorSunHours = toFiniteChartNumber(point?.outdoorSunHours);
  const supplementalHours = toFiniteChartNumber(point?.supplementalLightHours ?? point?.lampExtensionHours);
  const photoperiodHours = toFiniteChartNumber(point?.photoperiodHours);
  const targetDli = toFiniteChartNumber(point?.dliTargetMol);
  const minDli = toFiniteChartNumber(point?.dliMinMol);
  const targetHours = toFiniteChartNumber(point?.photoperiodTargetHours);
  const recLampPpfd = toFiniteChartNumber(point?.recommendedLampPpfdUmol);
  const recLampHours = toFiniteChartNumber(point?.recommendedLampHours);
  const recLampPowerTotalW = toFiniteChartNumber(point?.recommendedLampPowerTotalW);
  const cloudCover = toFiniteChartNumber(point?.cloudCover);
  const solarPpfdUmol = toFiniteChartNumber(point?.solarPpfdUmol);

  if (selectedMetric === 'totalDliMol') {
    if (Number.isFinite(naturalDli) || Number.isFinite(supplementalDli)) {
      html += `<br/><small style="color:#888">&nbsp;&nbsp;естественный: ${Number.isFinite(naturalDli) ? naturalDli.toFixed(1) : '0.0'} · досветка: ${Number.isFinite(supplementalDli) ? supplementalDli.toFixed(1) : '0.0'} mol/m²/д</small>`;
    }
    if (Number.isFinite(targetDli) || Number.isFinite(minDli)) {
      html += `<br/><small style="color:#888">Цель: ${Number.isFinite(targetDli) ? `${targetDli.toFixed(1)} mol/m²/д` : '—'}${Number.isFinite(minDli) ? ` (мин. ${minDli.toFixed(1)})` : ''}</small>`;
    }
  }
  if (selectedMetric === 'photoperiodHours') {
    if (Number.isFinite(outdoorSunHours) || Number.isFinite(supplementalHours)) {
      html += `<br/><small style="color:#888">&nbsp;&nbsp;естественный свет: ${Number.isFinite(outdoorSunHours) ? outdoorSunHours.toFixed(1) : '0.0'} ч · досветка: ${Number.isFinite(supplementalHours) ? supplementalHours.toFixed(1) : '0.0'} ч</small>`;
    }
    if (Number.isFinite(targetHours)) {
      html += `<br/><small style="color:#888">Цель фазы: ${targetHours.toFixed(1)} ч/день</small>`;
    }
  }
  if (selectedMetric === 'astronomicalDaylightHours') {
    html += `<br/><small style="color:#888">Астрономическая длина дня, без облачности и без ламп</small>`;
    if (Number.isFinite(outdoorSunHours)) {
      html += `<br/><small style="color:#888">&nbsp;&nbsp;наружное солнце с облачностью: ${outdoorSunHours.toFixed(1)} ч</small>`;
    }
    if (Number.isFinite(photoperiodHours)) {
      html += `<br/><small style="color:#888">&nbsp;&nbsp;итоговый фотопериод: ${photoperiodHours.toFixed(1)} ч</small>`;
    }
  }
  if (selectedMetric === 'outdoorSunHours') {
    html += `<br/><small style="color:#888">Наружные солнечные часы после учёта облачности</small>`;
    if (Number.isFinite(cloudCover)) {
      html += `<br/><small style="color:#888">&nbsp;&nbsp;облачность: ${(cloudCover * 100).toFixed(0)}%</small>`;
    }
    if (Number.isFinite(solarPpfdUmol)) {
      html += `<br/><small style="color:#888">&nbsp;&nbsp;солнечный PPFD: ${solarPpfdUmol.toFixed(0)} µmol/m²/s</small>`;
    }
  }
  if (Number.isFinite(recLampPpfd) && recLampPpfd > 0.1) {
    html += `<br/><small style="color:#888">Досветка: ${recLampPpfd.toFixed(0)} µmol/m²/s`;
    if (Number.isFinite(recLampHours) && recLampHours > 0) html += ` · ${recLampHours.toFixed(1)} ч/д`;
    if (Number.isFinite(recLampPowerTotalW) && recLampPowerTotalW > 0) html += ` · ${recLampPowerTotalW.toFixed(0)} W`;
    html += '</small>';
  }
  return html;
}

function formatRotationTooltip(point: any): string {
  let html = '';
  const rotFactor = Number(point?.rotationFactor);
  if (Number.isFinite(rotFactor) && rotFactor < 0.98) {
    const reasons = point?.rotationReasons as string[] | undefined;
    const rotState = point?.rotationState;
    const reasonText = reasons?.map((r: string) => ROTATION_REASON_LABELS[r] || r).join(', ') || '—';
    html += `<br/><small style="color:#a0522d">🔄 ${Math.round((1 - rotFactor) * 100)}% штрафа (${reasonText})</small>`;
    if (rotState) {
      const pp = Number(rotState.pathogenPressure);
      const sri = Number(rotState.soilRecoveryIndex);
      if (Number.isFinite(pp)) html += `<br/><small style="color:#888">&nbsp;&nbsp;Патогены: ${(pp * 100).toFixed(0)}%</small>`;
      if (Number.isFinite(sri)) html += `<br/><small style="color:#888">&nbsp;&nbsp;Восстановление: ${(sri * 100).toFixed(0)}%</small>`;
    }
  }
  return html;
}

function formatEtiolationTooltip(point: any, isEtiolMetric: boolean, isHeightMetric: boolean): string {
  let html = '';
  const stress = Number(point?.etiolation?.stress);
  const stemThickness = Number(point?.etiolation?.stemThickness);
  const heightBoost = Number(point?.etiolation?.heightGrowthMultiplier);
  if (Number.isFinite(stress) && stress > 0.01) {
    html += `<br/><small style="color:#eb2f96">Этиоляция: ${Math.round(stress * 100)}%</small>`;
    if (isEtiolMetric && Number.isFinite(stemThickness)) {
      html += `<br/><small style="color:#888">&nbsp;&nbsp;толщина стебля: ${Math.round(stemThickness * 100)}% от нормы</small>`;
    }
    if (isHeightMetric && Number.isFinite(heightBoost) && heightBoost > 1) {
      html += `<br/><small style="color:#888">&nbsp;&nbsp;добавка к росту: +${Math.round((heightBoost - 1) * 100)}%</small>`;
    }
  }
  return html;
}

// ────────────────────────────────────────
// Optimal zone overlay series
// ────────────────────────────────────────
function buildOptimalZoneSeries(
  points: any[],
  opts: {
    canShowSoilOptimalZone: boolean; canShowTempOptimalZone: boolean;
    canShowLightOptimalZone: boolean; canShowPhotoperiodOptimalZone: boolean;
    soilMin: number; soilMax: number; tempOptMin: number; tempOptMax: number;
  },
): any[] {
  const series: any[] = [];

  if (opts.canShowSoilOptimalZone) {
    series.push({
      name: 'Зона оптимума', type: 'line',
      data: points.map(() => null), showSymbol: false, silent: true,
      lineStyle: { opacity: 0 }, tooltip: { show: false },
      markArea: {
        silent: true, itemStyle: { color: 'rgba(82, 196, 26, 0.12)' },
        label: { show: true, color: '#389e0d', formatter: `зона оптимума ${opts.soilMin.toFixed(0)}–${opts.soilMax.toFixed(0)}%` },
        data: [[{ yAxis: opts.soilMin }, { yAxis: opts.soilMax }]],
      },
    });
  }
  if (opts.canShowTempOptimalZone) {
    series.push({
      name: 'Зона оптимума температуры', type: 'line',
      data: points.map(() => null), showSymbol: false, silent: true,
      lineStyle: { opacity: 0 }, tooltip: { show: false },
      markArea: {
        silent: true, itemStyle: { color: 'rgba(245, 34, 45, 0.10)' },
        label: { show: true, color: '#cf1322', formatter: `зона оптимума ${opts.tempOptMin.toFixed(0)}–${opts.tempOptMax.toFixed(0)}°C` },
        data: [[{ yAxis: opts.tempOptMin }, { yAxis: opts.tempOptMax }]],
      },
    });
  }
  if (opts.canShowLightOptimalZone) {
    series.push(
      {
        name: 'Минимум DLI', type: 'line', showSymbol: false, silent: true, tooltip: { show: false },
        lineStyle: { color: '#faad14', type: 'dashed', width: 1 }, itemStyle: { color: '#faad14' },
        data: points.map((p: any) => {
          const v = Number(p?.dliMinMol);
          return Number.isFinite(v) ? normalizeChartNumber(v, 4) : null;
        }),
      },
      {
        name: 'Цель DLI', type: 'line', showSymbol: false, silent: true, tooltip: { show: false },
        lineStyle: { color: '#ad6800', type: 'dashed', width: 1 }, itemStyle: { color: '#ad6800' },
        data: points.map((p: any) => {
          const v = Number(p?.dliTargetMol);
          return Number.isFinite(v) ? normalizeChartNumber(v, 4) : null;
        }),
      },
    );
  }
  if (opts.canShowPhotoperiodOptimalZone) {
    series.push({
      name: 'Цель фотопериода', type: 'line', showSymbol: false, silent: true, tooltip: { show: false },
      lineStyle: { color: '#ad6800', type: 'dashed', width: 1 }, itemStyle: { color: '#ad6800' },
      data: points.map((p: any) => {
        const v = Number(p?.photoperiodTargetHours);
        return Number.isFinite(v) && v > 0 ? normalizeChartNumber(v, 4) : null;
      }),
    });
  }

  return series;
}
